"use client";

import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ParticleField, { type ParticleShape } from "./ParticleField";

type Step = {
  id: string;
  tag: string;
  title: string;
  description: string;
  code: string;
  metrics: { nodes: number; tps: number; latency: number };
  shape: ParticleShape;
};

const STEPS: Step[] = [
  {
    id: "01",
    tag: "INIT_01",
    title: "DISTRIBUTED",
    description: "Independent compute nodes synchronized into a unified learning domain. Model intelligence is partitioned, propagated, and reinforced across the network.",
    code: `const swarm = await initSwarm({\n  nodes: 1240,\n  shards: 64\n});\n// ✓ CONSENSUS REACHED`,
    metrics: { nodes: 1240, tps: 15420, latency: 12 },
    shape: "text",
  },
  {
    id: "02",
    tag: "SYNC_02",
    title: "INFERENCE",
    description: "Live inference streams executed across adaptive compute clusters. Capacity responds instantly to external demand.",
    code: `async function inference(data) {\n  const result = await model.predict(\n    data, { timeout: 50 }\n  );\n  return result; // 12ms avg\n}`,
    metrics: { nodes: 1180, tps: 18960, latency: 8 },
    shape: "sphere",
  },
  {
    id: "03",
    tag: "TRAIN_03",
    title: "ADAPTIVE",
    description: "Adaptive optimization processes continuously refine model behavior. Execution paths adapt continuously under live performance feedback.",
    code: `while (accuracy < 99.9) {\n  gradients = backprop(loss);\n  weights = optimize(gradients);\n  // Auto-scaling active\n}`,
    metrics: { nodes: 1350, tps: 22140, latency: 15 },
    shape: "cube",
  },
  {
    id: "04",
    tag: "SECURE_04",
    title: "VERIFIED",
    description: "Execution integrity enforced through cryptographic verification layers. Model logic and data flows remain isolated.",
    code: `const encrypted = await zkProof({\n  model: weights,\n  validation: true\n});\n// 🔒 HASH: 0x8F2A...VERIFIED`,
    metrics: { nodes: 1290, tps: 19850, latency: 10 },
    shape: "ring",
  },
  {
    id: "05",
    tag: "SCALE_05",
    title: "SCALABILITY",
    description: "An elastic execution grid designed for rapid expansion. Compute density adjusts in real time allowing workloads to transition to global-scale.",
    code: `if (demand > threshold) {\n  await scale.up({\n    target: demand * 1.5\n  });\n  // ⚡ SCALING COMPLETE\n}`,
    metrics: { nodes: 1420, tps: 25680, latency: 9 },
    shape: "grid",
  },
];

// --- HELPER COMPONENTS ---

// 1. Kinetic Background (Hex Wall)
const KineticBackground = () => {
  const [data, setData] = useState<string[]>([]);
  useEffect(() => {
    // Generate static hex data
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setData(Array.from({ length: 80 }).map(() =>
      Math.random().toString(16).toUpperCase().substring(2, 10)
    ));
  }, []);

  return (
    <div className="absolute inset-0 z-0 opacity-[0.06] select-none pointer-events-none font-mono text-[10px] flex flex-wrap gap-x-6 gap-y-3 p-4 overflow-hidden">
      {data.map((hex, i) => (
        <span key={i} className="whitespace-nowrap text-cyan-500">
          0x{hex}
        </span>
      ))}
      <div className="absolute inset-0 bg-[url('/noise.svg')] opacity-20 mix-blend-overlay" />
    </div>
  );
};

// 2. Glitch Title (Cestus Style)
const GlitchTitle = ({ text }: { text: string }) => {
  return (
    <div className="relative w-full h-[14vw] md:h-[7vw] mb-2 md:mb-6">
      <AnimatePresence mode="wait">
        <motion.div
          key={text}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0"
        >
          {/* Layer 1: Ghost Stroke */}
          <h1
            className="absolute inset-0 text-[14vw] md:text-[7vw] font-black leading-none tracking-tighter text-transparent uppercase select-none"
            style={{ WebkitTextStroke: '1px rgba(255,255,255,0.15)' }}
          >
            {text}
          </h1>

          {/* Layer 2: Filled Text with Clip Reveal */}
          <motion.h1
            initial={{ clipPath: "inset(0 100% 0 0)" }}
            animate={{ clipPath: "inset(0 0% 0 0)" }}
            transition={{ duration: 0.5, ease: "circOut" }}
            className="absolute inset-0 text-[14vw] md:text-[7vw] font-black leading-none tracking-tighter text-white uppercase select-none z-10"
            style={{ textShadow: '0 0 30px rgba(6,182,212,0.5)' }}
          >
            {text}
          </motion.h1>

          {/* Layer 3: Glitch Clones */}
          <motion.h1
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 1, 0, 1, 0], x: [-2, 2, -1, 0] }}
            transition={{ duration: 0.4, times: [0, 0.2, 0.4, 0.6, 1] }}
            className="absolute top-0 left-1 text-[14vw] md:text-[7vw] font-black leading-none tracking-tighter text-fuchsia-500/75 uppercase mix-blend-screen select-none"
          >
            {text}
          </motion.h1>
          <motion.h1
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 1, 0, 1, 0], x: [2, -2, 1, 0] }}
            transition={{ duration: 0.4, delay: 0.1, times: [0, 0.2, 0.4, 0.6, 1] }}
            className="absolute top-0 -left-1 text-[14vw] md:text-[7vw] font-black leading-none tracking-tighter text-cyan-500/75 uppercase mix-blend-screen select-none"
          >
            {text}
          </motion.h1>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

// 3. Typewriter Effect
const Typewriter = ({ text }: { text: string }) => {
  const [display, setDisplay] = useState("");
  useEffect(() => {
    let i = 0;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setDisplay("");
    const timer = setInterval(() => {
      if (i < text.length) {
        setDisplay((prev) => prev + text.charAt(i));
        i++;
      } else {
        clearInterval(timer);
      }
    }, 5); // Fast typing for code
    return () => clearInterval(timer);
  }, [text]);

  return <>{display}<span className="animate-pulse text-cyan-500">_</span></>;
};

// --- MAIN COMPONENT ---
export default function SystemFailure() {
  const [activeStep, setActiveStep] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const currentStep = STEPS[activeStep];

  // Scroll Sync Logic
  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const totalHeight = rect.height - windowHeight;
      // Calculate scroll progress (0 to 1)
      const progress = Math.abs(Math.min(0, rect.top)) / totalHeight;
      
      const idx = Math.min(
        Math.max(Math.floor(progress * STEPS.length), 0),
        STEPS.length - 1
      );
      setActiveStep(idx);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div 
      ref={containerRef} 
      className="relative w-full h-[600vh] bg-[#02040a] text-white selection:bg-fuchsia-500 selection:text-white font-sans"
    >


      {/* STICKY CONTAINER */}
      <div className="sticky top-0 h-screen w-full flex flex-col md:flex-row overflow-hidden">
        
        {/* --- LEFT PANEL (UI) --- */}
        <div className="relative w-full md:w-[45%] h-1/2 md:h-full z-20 flex flex-col justify-center px-6 md:px-12 border-r border-white/5 bg-[#02040a]/90 backdrop-blur-xl">
          
          {/* Background Elements */}
          <KineticBackground />
          <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent animate-pulse" />

          {/* CONTENT WRAPPER */}
          <div className="relative z-10 w-full max-w-xl">
            
            {/* 1. TOP HUD TAG */}
            <div className="flex items-center gap-3 mb-4 md:mb-8">
               <div className="h-[1px] w-8 bg-cyan-500/50" />
               <span className="font-mono text-[10px] tracking-[0.3em] text-cyan-400">
                 SYS_PHASE_{currentStep.id}
               </span>
               <div className="h-[1px] flex-1 bg-gradient-to-r from-cyan-500/50 to-transparent" />
            </div>

            {/* 2. GLITCH TITLE */}
            <GlitchTitle text={currentStep.title} />

            {/* 3. TERMINAL DESCRIPTION BOX */}
            <AnimatePresence mode="wait">
              <motion.div 
                key={currentStep.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="relative p-6 border border-white/10 bg-white/[0.02] backdrop-blur-sm rounded-lg overflow-hidden group"
              >
                {/* Decorative Corners */}
                <div className="absolute top-0 left-0 w-2 h-2 border-l border-t border-cyan-500" />
                <div className="absolute top-0 right-0 w-2 h-2 border-r border-t border-fuchsia-500" />
                <div className="absolute bottom-0 left-0 w-2 h-2 border-l border-b border-cyan-500" />
                <div className="absolute bottom-0 right-0 w-2 h-2 border-r border-b border-fuchsia-500" />

                {/* Scanline */}
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-cyan-500/[0.03] to-transparent bg-[length:100%_4px] pointer-events-none" />

                {/* Header */}
                <div className="flex justify-between items-center mb-4 border-b border-white/10 pb-2">
                  <span className="font-mono text-xs text-fuchsia-400 tracking-widest">
                    {`// ${currentStep.tag}`}
                  </span>
                  <span className="flex items-center gap-2 font-mono text-[10px] text-white/75 uppercase">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                    Live Feed
                  </span>
                </div>

                {/* Body Text */}
                <p className="font-mono text-sm leading-relaxed text-blue-100/80 mb-4">
                  {currentStep.description}
                </p>

                {/* Code Block */}
                <div className="bg-black/40 p-3 font-mono text-[10px] text-cyan-300/90 border-l-2 border-fuchsia-500/30 overflow-x-hidden whitespace-pre-wrap">
                   <Typewriter text={currentStep.code} />
                </div>
              </motion.div>
            </AnimatePresence>

            {/* 4. METRICS VISUALIZER */}
            <div className="mt-6 md:mt-8 grid grid-cols-3 gap-4">
              {[
                { label: "NODES", val: currentStep.metrics.nodes, max: 2000, color: "bg-cyan-500" },
                { label: "TPS", val: currentStep.metrics.tps, max: 30000, color: "bg-violet-500" },
                { label: "LATENCY", val: currentStep.metrics.latency, max: 50, color: "bg-fuchsia-500" }
              ].map((m, i) => (
                <div key={i} className="flex flex-col gap-1">
                  <span className="font-mono text-[10px] uppercase text-white/75 tracking-widest">{m.label}</span>
                  <div className="relative h-1 w-full bg-white/10 rounded-full overflow-hidden">
                    <motion.div 
                      initial={{ scaleX: 0 }}
                      animate={{ scaleX: m.val / m.max }}
                      transition={{ duration: 1, ease: "circOut" }}
                      className={`absolute top-0 left-0 h-full w-full origin-left rounded-full ${m.color}`} 
                    />
                  </div>
                  <span className="font-mono text-xs text-white/80">{m.val}</span>
                </div>
              ))}
            </div>

            {/* 5. PROGRESS BAR */}
            <div className="absolute -bottom-12 left-0 w-full">
               <div className="relative h-px w-full bg-white/10">
                 <motion.div 
                   className="absolute top-0 left-0 h-full w-full origin-left bg-gradient-to-r from-cyan-500 via-violet-500 to-fuchsia-500 shadow-[0_0_15px_#d946ef]"
                   animate={{ scaleX: (activeStep + 1) / STEPS.length }}
                 />
               </div>
            </div>

          </div>
        </div>

        {/* --- RIGHT PANEL (PARTICLE SYSTEM) --- */}
        <div className="relative w-full md:w-[55%] h-1/2 md:h-full bg-[#02040a] overflow-hidden">
            <ParticleField
              shape={currentStep.shape}
              text={currentStep.shape === 'text' ? currentStep.title : ''}
            />
            
            {/* Corner Deco */}
            <div className="absolute top-6 right-6 z-30 flex gap-2">
                <div className="w-2 h-2 border border-cyan-500/30" />
                <div className="w-2 h-2 border border-fuchsia-500/50" />
            </div>
            
            <div className="absolute bottom-10 right-10 pointer-events-none opacity-40">
               <div className="w-12 h-12 rounded-full border border-dashed border-fuchsia-500/30 flex items-center justify-center animate-spin-slow">
                 <div className="w-1 h-1 bg-fuchsia-500" />
               </div>
            </div>
        </div>

      </div>
    </div>
  );
}
